import type { ContextSelectionExpression } from "../../contracts/src/contextSelection.ts";
import { parseFilesystemMemberReference } from "../../contracts/src/filesystemMemberReference.ts";
export {
  parseFilesystemMemberReference,
  type FilesystemMemberReference,
} from "../../contracts/src/filesystemMemberReference.ts";

const choiceKey = (choice: ContextSelectionExpression) => `${choice.memberId}\0${choice.kind}`;

export function normalizeContextSelection(
  expressions: ReadonlyArray<ContextSelectionExpression>,
): ReadonlyArray<ContextSelectionExpression> {
  const result = new Map<string, ContextSelectionExpression>();
  for (const expression of expressions) {
    const memberId = expression.memberId;
    if (!memberId || memberId.length > 4096 || memberId.includes("\0") || memberId.endsWith("/"))
      throw new Error("The selection contains an invalid member reference.");
    if (expression.kind === "folder" && expression.delivery !== "reference")
      throw new Error(
        "A folder is a searchable reference. Include individual documents as working knowledge.",
      );
    result.set(choiceKey(expression), {
      kind: expression.kind,
      memberId,
      selected: expression.selected,
      delivery: expression.delivery,
    });
  }
  return [...result.values()].sort((a, b) =>
    choiceKey(a) < choiceKey(b) ? -1 : choiceKey(a) > choiceKey(b) ? 1 : 0,
  );
}

/** The accepted operation retains its direction. No later state toggles it. */
export function moveContextSelection(
  current: ReadonlyArray<ContextSelectionExpression>,
  move: {
    readonly expressions: ReadonlyArray<ContextSelectionExpression>;
    readonly replace?: boolean | undefined;
    readonly clearGroups?: ReadonlyArray<string> | undefined;
  },
): ReadonlyArray<ContextSelectionExpression> {
  const retained = move.replace
    ? []
    : current.filter(
        (choice) => !move.clearGroups?.some((group) => choice.memberId.startsWith(`${group}:`)),
      );
  return normalizeContextSelection([...retained, ...move.expressions]);
}

/** Exact members beat folders at equal specificity; otherwise the deepest
 * explicit ancestor wins. An exclusion survives overlapping folder grants. */
export function contextChoiceForMember(
  expressions: ReadonlyArray<ContextSelectionExpression>,
  memberId: string,
  ancestors?: ReadonlyArray<string>,
): ContextSelectionExpression | undefined {
  let chosen: ContextSelectionExpression | undefined;
  let closest = Number.POSITIVE_INFINITY;
  for (const choice of expressions) {
    if (parseFilesystemMemberReference(choice.memberId)) {
      const distance =
        choice.memberId === memberId
          ? 0
          : choice.kind === "folder"
            ? (ancestors?.indexOf(choice.memberId) ?? -1) + 1
            : 0;
      if (choice.memberId !== memberId && distance <= 0) continue;
      if (distance < closest) {
        chosen = choice;
        closest = distance;
      }
      continue;
    }
    if (
      choice.memberId !== memberId &&
      !(
        choice.kind === "folder" &&
        (ancestors?.includes(choice.memberId) ||
          (!parseFilesystemMemberReference(choice.memberId) &&
            memberId.startsWith(`${choice.memberId}/`)))
      )
    )
      continue;
    if (
      !chosen ||
      choice.memberId.length > chosen.memberId.length ||
      (choice.memberId.length === chosen.memberId.length && choice.kind === "member")
    )
      chosen = choice;
  }
  return chosen;
}

export function contextSelectionAdmits(
  expressions: ReadonlyArray<ContextSelectionExpression>,
  memberId: string,
): boolean {
  return contextChoiceForMember(expressions, memberId)?.selected === true;
}

/** Receipts must already be canonical. Never normalize a received boundary into permission. */
export function parseContextSelectionExpressions(
  value: unknown,
): ReadonlyArray<ContextSelectionExpression> | null {
  if (!Array.isArray(value)) return null;
  for (const choice of value) {
    if (
      !choice ||
      typeof choice !== "object" ||
      Array.isArray(choice) ||
      typeof choice.memberId !== "string" ||
      !["member", "folder"].includes(choice.kind) ||
      typeof choice.selected !== "boolean" ||
      !["include", "reference"].includes(choice.delivery) ||
      Object.keys(choice).some((key) => !["memberId", "kind", "selected", "delivery"].includes(key))
    )
      return null;
  }
  try {
    const normalized = normalizeContextSelection(value);
    if (
      normalized.length !== value.length ||
      normalized.some(
        (choice, index) =>
          choice.memberId !== value[index].memberId ||
          choice.kind !== value[index].kind ||
          choice.selected !== value[index].selected ||
          choice.delivery !== value[index].delivery,
      )
    )
      return null;
    return normalized;
  } catch {
    return null;
  }
}

/** Stable property order for hashes across JSON and Convex value transport.
 * Array order and every choice remain exact; this does not admit or repair choices. */
export function contextSelectionDigestValue(
  expressions: ReadonlyArray<ContextSelectionExpression> | undefined,
) {
  return expressions?.map((choice) => ({
    kind: choice.kind,
    memberId: choice.memberId,
    selected: choice.selected,
    delivery: choice.delivery,
  }));
}
